'use client';

import React, { useState, useEffect, useRef, Suspense } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';
import AppLayout from '@/components/AppLayout';
import { ChevronLeft, Bookmark, Share2, MapPin, Clock, Globe, DollarSign, Users, Calendar, Tag, Crown, Check, BookMarked, Type, Sun, Moon } from 'lucide-react';
import { articlesData, defaultArticleSlug, type FullArticle, type ArticleSection } from './articlesData';
import { extraordinaryPlacesArticles } from './extraordinaryPlacesData';
import { luxuryTravelArticles } from './luxuryTravelData';
import { artCollectingArticles } from './artCollectingData';
import { whiskySpiritArticles } from './whiskySpiritData';
import { watchesHorologyArticles } from './watchesHorologyData';
import { jewelleryGemsArticles } from './jewelleryGemsData';
import { privateClubsArticles } from './privateClubsData';
import { royaltyDynastiesArticles } from './royaltyDynastiesData';
import { architectureDesignArticles } from './architectureDesignData';
import { automobilesArticles } from './automobilesData';
import { aviationYachtsArticles } from './aviationYachtsData';
import SubscriptionModal from '@/components/SubscriptionModal';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';

// Combined lookup across all article collections
const allArticles: Record<string, FullArticle> = {
  ...articlesData,
  ...extraordinaryPlacesArticles,
  ...luxuryTravelArticles,
  ...artCollectingArticles,
  ...whiskySpiritArticles,
  ...watchesHorologyArticles,
  ...jewelleryGemsArticles,
  ...privateClubsArticles,
  ...royaltyDynastiesArticles,
  ...architectureDesignArticles,
  ...automobilesArticles,
  ...aviationYachtsArticles,
};

function SectionBlock({ section }: { section: ArticleSection }) {
  if (section.type === 'paragraph') {
    return (
      <p className="text-[14px] text-[var(--foreground)] leading-[1.85] mb-5 font-light">
        {section.content}
      </p>
    );
  }
  if (section.type === 'subheading') {
    return (
      <h4 className="text-[15px] font-bold text-[var(--foreground)] mt-7 mb-3 tracking-wide border-l-2 border-[var(--gold)] pl-3">
        {section.content}
      </h4>
    );
  }
  if (section.type === 'pullquote') {
    return (
      <blockquote className="border-l-3 border-[var(--gold)] pl-5 my-7 bg-[var(--secondary)] py-4 pr-4 rounded-r">
        <p className="text-[15px] italic text-[var(--foreground)] leading-relaxed mb-2 font-medium">
          "{section.content}"
        </p>
        {section.label && (
          <p className="text-[11px] font-semibold text-[var(--gold)] tracking-[0.1em] uppercase">
            — {section.label}
          </p>
        )}
      </blockquote>
    );
  }
  if (section.type === 'list' && section.items) {
    return (
      <ul className="space-y-2 mb-5 ml-1">
        {section.items.map((item, i) => (
          <li key={i} className="flex items-start gap-3 text-[13px] text-[var(--foreground)]">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--gold)] flex-shrink-0 mt-2" />
            <span className="leading-relaxed">{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (section.type === 'factbox') {
    return (
      <div className="bg-gradient-to-br from-[var(--gold)]/5 to-[var(--secondary)] border border-[var(--gold)]/30 rounded-lg p-5 my-6">
        {section.label && (
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[var(--gold)] mb-2">{section.label}</p>
        )}
        <p className="text-[13px] text-[var(--foreground)] leading-relaxed">{section.content}</p>
      </div>
    );
  }
  return null;
}

function ArticlePageContent() {
  const searchParams = useSearchParams();
  const slug = searchParams.get('slug') || defaultArticleSlug;
  const article: FullArticle = allArticles[slug] || allArticles[defaultArticleSlug];

  const [saved, setSaved] = useState(false);
  const [savingArticle, setSavingArticle] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const [showPaywall, setShowPaywall] = useState(false);
  const [readProgress, setReadProgress] = useState(0);
  const [fontSize, setFontSize] = useState<'sm' | 'md' | 'lg'>('md');
  const [darkMode, setDarkMode] = useState(false);
  const [showReadingBar, setShowReadingBar] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const { isSubscribed, loading, user } = useAuth();
  const supabase = createClient();

  // Paywall disabled — all content freely accessible
  const paywallActive = false;

  // Reading progress tracker
  useEffect(() => {
    const handleScroll = () => {
      const el = contentRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const totalHeight = el.offsetHeight;
      const scrolled = Math.max(0, -rect.top);
      const progress = Math.min(100, Math.round((scrolled / totalHeight) * 100));
      setReadProgress(progress);
      setShowReadingBar(scrolled > 100);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check if already saved
  useEffect(() => {
    if (!user || !slug) return;
    const checkSaved = async () => {
      const { data } = await supabase
        .from('saved_articles')
        .select('id')
        .eq('user_id', user.id)
        .eq('article_slug', slug)
        .maybeSingle();
      setSaved(!!data);
    };
    checkSaved();
  }, [user, slug]);

  const handleSaveArticle = async () => {
    if (!user) {
      toast.error('Sign in to save articles');
      return;
    }
    setSavingArticle(true);
    try {
      if (saved) {
        await supabase.from('saved_articles').delete().eq('user_id', user.id).eq('article_slug', slug);
        setSaved(false);
        toast.success('Removed from library');
      } else {
        await supabase.from('saved_articles').upsert({
          user_id: user.id,
          article_slug: slug,
          article_title: article?.title || slug,
        }, { onConflict: 'user_id,article_slug' });
        setSaved(true);
        toast.success('Saved to your library', { description: article?.title });
      }
    } catch {
      toast.error('Failed to save. Please try again.');
    }
    setSavingArticle(false);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/article?slug=${slug}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: article?.title, url });
      } catch {}
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    }
  };

  const toggleItem = (id: string) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const fontSizeClass = fontSize === 'sm' ? 'text-[12px]' : fontSize === 'lg' ? 'text-[16px]' : 'text-[14px]';

  return (
    <AppLayout currentPath="/article">
      <div
        ref={contentRef}
        className={`min-h-screen max-w-2xl mx-auto lg:max-w-3xl xl:max-w-4xl transition-colors ${darkMode ? 'bg-[#0f0f0f]' : 'bg-white'}`}
      >
        {/* Reading Progress Bar */}
        {showReadingBar && (
          <div className="fixed top-0 left-0 right-0 z-[60] h-0.5 bg-[var(--border)]">
            <div
              className="h-full bg-[var(--gold)] transition-all duration-150"
              style={{ width: `${readProgress}%` }}
            />
          </div>
        )}

        {/* Mobile Header */}
        <div className={`flex items-center justify-between px-4 py-3 border-b ${darkMode ? 'border-white/10 bg-[#0f0f0f]' : 'border-[var(--border)] bg-white'} sticky top-0 z-40`}>
          <Link href="/category" className="p-1">
            <ChevronLeft size={20} className={darkMode ? 'text-white' : 'text-[var(--foreground)]'} />
          </Link>
          <div className="flex items-center gap-1.5">
            {/* Crown members badge */}
            <div className="flex items-center gap-1 px-2 py-1 bg-[var(--gold)]/10 border border-[var(--gold)]/30 rounded-full">
              <Crown size={11} className="text-[var(--gold)]" fill="currentColor" fillOpacity={0.5} />
              <span className="text-[9px] font-bold tracking-[0.1em] uppercase text-[var(--gold)]">Members</span>
            </div>

            {/* Font size toggle */}
            <button
              onClick={() => setFontSize(f => f === 'sm' ? 'md' : f === 'md' ? 'lg' : 'sm')}
              className={`p-1.5 rounded transition-colors ${darkMode ? 'text-white/60 hover:text-white' : 'text-[var(--slate-muted)] hover:text-[var(--foreground)]'}`}
              title="Change font size"
            >
              <Type size={16} />
            </button>

            {/* Dark mode toggle */}
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`p-1.5 rounded transition-colors ${darkMode ? 'text-[var(--gold)]' : 'text-[var(--slate-muted)] hover:text-[var(--foreground)]'}`}
              title="Toggle reading mode"
            >
              {darkMode ? <Sun size={16} /> : <Moon size={16} />}
            </button>

            {/* Save button */}
            <button
              onClick={handleSaveArticle}
              disabled={savingArticle}
              className="p-1.5"
              title={saved ? 'Remove from library' : 'Save to library'}
            >
              {saved
                ? <BookMarked size={18} className="text-[var(--gold)] fill-[var(--gold)]" />
                : <Bookmark size={18} className={darkMode ? 'text-white/70' : 'text-[var(--foreground)]'} />
              }
            </button>

            {/* Share */}
            <button onClick={handleShare} className="p-1.5">
              <Share2 size={18} className={darkMode ? 'text-white/70' : 'text-[var(--foreground)]'} />
            </button>
          </div>
        </div>

        {/* Reading progress indicator (inline) */}
        <div className={`flex items-center gap-3 px-4 lg:px-6 py-2 border-b ${darkMode ? 'border-white/10' : 'border-[var(--border)]'}`}>
          <div className={`flex-1 h-1 rounded-full ${darkMode ? 'bg-white/10' : 'bg-[var(--border)]'}`}>
            <div
              className="h-full bg-[var(--gold)] rounded-full transition-all duration-300"
              style={{ width: `${readProgress}%` }}
            />
          </div>
          <span className={`text-[10px] font-semibold tracking-wide ${darkMode ? 'text-white/40' : 'text-[var(--slate-muted)]'}`}>
            {readProgress}% read
          </span>
        </div>

        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 px-4 lg:px-6 pt-4 pb-2">
          {article?.breadcrumb?.map((crumb, i) => (
            <React.Fragment key={crumb}>
              <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-[var(--gold)]">{crumb}</span>
              {i < article?.breadcrumb?.length - 1 && <span className="text-[var(--slate-muted)] text-[11px]">›</span>}
            </React.Fragment>
          ))}
        </div>

        {/* Title */}
        <div className="px-4 lg:px-6 pb-4">
          <h1 className={`font-editorial text-2xl lg:text-3xl font-bold leading-tight ${darkMode ? 'text-white' : 'text-[var(--foreground)]'}`}>
            {article?.title}
          </h1>
          <p className={`text-sm mt-2 leading-relaxed ${darkMode ? 'text-white/60' : 'text-[var(--slate-muted)]'}`}>{article?.tagline}</p>
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <div className="w-6 h-6 rounded-full bg-[var(--gold)]/20 flex items-center justify-center">
              <span className="text-[9px] font-bold text-[var(--gold)]">PD</span>
            </div>
            <span className={`text-[12px] ${darkMode ? 'text-white/60' : 'text-[var(--slate-muted)]'}`}>By {article?.author}</span>
            <span className={`text-[11px] ${darkMode ? 'text-white/30' : 'text-[var(--slate-muted)]'}`}>·</span>
            <span className={`text-[12px] ${darkMode ? 'text-white/60' : 'text-[var(--slate-muted)]'}`}>{article?.date}</span>
            <span className={`text-[11px] ${darkMode ? 'text-white/30' : 'text-[var(--slate-muted)]'}`}>·</span>
            <div className="flex items-center gap-1">
              <Clock size={11} className="text-[var(--gold)]" />
              <span className={`text-[12px] ${darkMode ? 'text-white/60' : 'text-[var(--slate-muted)]'}`}>{article?.readTime}</span>
            </div>
          </div>
        </div>

        {/* Hero Image */}
        <div className="relative h-[220px] lg:h-[360px] mx-4 lg:mx-6 rounded-lg overflow-hidden mb-6">
          <AppImage src={article?.heroImage} alt={article?.heroAlt} fill className="object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-black/70 backdrop-blur-sm border border-[#c9a84c]/50 rounded-full px-3 py-1.5">
            <Crown size={13} className="text-[#c9a84c]" fill="#c9a84c" fillOpacity={0.6} />
            <span className="text-[10px] font-bold tracking-[0.1em] uppercase text-[#c9a84c]">Premium</span>
          </div>
          {/* Save overlay button */}
          <button
            onClick={handleSaveArticle}
            className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-black/70 backdrop-blur-sm border border-white/20 rounded-full px-3 py-1.5 hover:border-[var(--gold)]/50 transition-colors"
          >
            {saved
              ? <><BookMarked size={12} className="text-[var(--gold)]" /><span className="text-[10px] text-[var(--gold)] font-semibold">Saved</span></>
              : <><Bookmark size={12} className="text-white/80" /><span className="text-[10px] text-white/80 font-semibold">Save</span></>
            }
          </button>
        </div>

        {/* The Discovery */}
        <div className={`px-4 lg:px-6 mb-6 p-5 mx-4 lg:mx-6 rounded-lg border ${darkMode ? 'bg-white/5 border-white/10' : 'bg-[var(--secondary)] border-[var(--border)]'}`}>
          <h2 className="text-[11px] font-bold tracking-[0.15em] uppercase text-[var(--gold)] mb-2">THE DISCOVERY</h2>
          <p className={`text-[15px] leading-relaxed font-medium ${darkMode ? 'text-white/90' : 'text-[var(--foreground)]'}`}>{article?.discovery}</p>
        </div>

        {/* Blurred paywall content gate */}
        <div className={`relative ${paywallActive ? 'overflow-hidden' : ''}`}>
          <div className={paywallActive ? 'blur-[3px] pointer-events-none select-none' : ''}>
            {/* Items */}
            <div className="px-4 lg:px-6 space-y-6 mb-6">
              {article?.items?.map((item) => {
                const isExpanded = expandedItems[item.id] !== false;
                return (
                  <div key={item.id} className={`border rounded-lg overflow-hidden ${darkMode ? 'border-white/10' : 'border-[var(--border)]'}`}>
                    <button
                      onClick={() => toggleItem(item.id)}
                      className={`w-full text-left p-4 border-b transition-colors ${darkMode ? 'border-white/10 hover:bg-white/5' : 'border-[var(--border)] hover:bg-[var(--secondary)]'}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          {item.number && (
                            <p className="text-[11px] font-bold tracking-[0.1em] text-[var(--gold)]">{item.number}.</p>
                          )}
                          <h3 className={`font-editorial text-lg font-bold leading-tight mt-0.5 ${darkMode ? 'text-white' : 'text-[var(--foreground)]'}`}>{item?.name}</h3>
                          {item?.location && (
                            <div className="flex items-center gap-1.5 mt-1">
                              <MapPin size={11} className="text-[var(--slate-muted)]" />
                              <span className={`text-[11px] ${darkMode ? 'text-white/50' : 'text-[var(--slate-muted)]'}`}>{item?.location}</span>
                            </div>
                          )}
                        </div>
                        <span className={`text-lg mt-1 ${darkMode ? 'text-white/50' : 'text-[var(--slate-muted)]'}`}>{isExpanded ? '−' : '+'}</span>
                      </div>
                    </button>

                    {isExpanded && (
                      <>
                        <div className="relative h-[200px] lg:h-[280px]">
                          <AppImage src={item?.image} alt={item?.alt} fill className="object-cover" />
                        </div>
                        <div className={`p-4 border-b ${darkMode ? 'border-white/10' : 'border-[var(--border)]'}`}>
                          <p className={`${fontSizeClass} leading-relaxed ${darkMode ? 'text-white/80' : 'text-[var(--foreground)]'}`}>{item?.description}</p>
                          <div className="mt-4">
                            <p className={`text-[11px] font-bold tracking-[0.1em] uppercase mb-2 ${darkMode ? 'text-white' : 'text-[var(--foreground)]'}`}>Highlights</p>
                            <ul className="space-y-2">
                              {item?.highlights?.map((h) => (
                                <li key={h} className={`flex items-start gap-2 ${fontSizeClass} ${darkMode ? 'text-white/80' : 'text-[var(--foreground)]'}`}>
                                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--gold)] flex-shrink-0 mt-1.5" />
                                  {h}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                        {item?.body && item.body.length > 0 && (
                          <div className={`p-4 border-b ${darkMode ? 'border-white/10' : 'border-[var(--border)]'}`}>
                            <h4 className="text-[11px] font-bold tracking-[0.15em] uppercase text-[var(--gold)] mb-4">IN DEPTH</h4>
                            {item.body.map((section, idx) => (
                              <SectionBlock key={idx} section={section} />
                            ))}
                          </div>
                        )}
                        <div className={`p-4 border-b ${darkMode ? 'border-white/10' : 'border-[var(--border)]'}`}>
                          <h4 className={`text-[12px] font-bold mb-2 ${darkMode ? 'text-white' : 'text-[var(--foreground)]'}`}>What Makes It Special</h4>
                          <p className={`text-[12px] leading-relaxed ${darkMode ? 'text-white/60' : 'text-[var(--slate-muted)]'}`}>{item?.whatMakesSpecial}</p>
                          {item?.interiorImage && (
                            <div className="relative h-[140px] mt-3 rounded overflow-hidden">
                              <AppImage src={item?.interiorImage} alt={item?.interiorAlt || ''} fill className="object-cover" />
                            </div>
                          )}
                        </div>
                        <div className={`p-4 border-b ${darkMode ? 'border-white/10 bg-white/5' : 'border-[var(--border)] bg-[var(--secondary)]'}`}>
                          <h4 className={`text-[11px] font-bold tracking-[0.12em] uppercase mb-3 ${darkMode ? 'text-white' : 'text-[var(--foreground)]'}`}>ESSENTIAL DETAILS</h4>
                          <div className="space-y-2.5">
                            {item?.essentialDetails?.location && (
                              <div className="flex gap-3">
                                <MapPin size={14} className="text-[var(--gold)] flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className={`text-[10px] font-semibold tracking-[0.08em] uppercase ${darkMode ? 'text-white/40' : 'text-[var(--slate-muted)]'}`}>Location</p>
                                  <p className={`text-[12px] mt-0.5 ${darkMode ? 'text-white/80' : 'text-[var(--foreground)]'}`}>{item.essentialDetails.location}</p>
                                </div>
                              </div>
                            )}
                            {item?.essentialDetails?.bestTime && (
                              <div className="flex gap-3">
                                <Calendar size={14} className="text-[var(--gold)] flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className={`text-[10px] font-semibold tracking-[0.08em] uppercase ${darkMode ? 'text-white/40' : 'text-[var(--slate-muted)]'}`}>Best Time to Visit</p>
                                  <p className={`text-[12px] mt-0.5 ${darkMode ? 'text-white/80' : 'text-[var(--foreground)]'}`}>{item.essentialDetails.bestTime}</p>
                                </div>
                              </div>
                            )}
                            {item?.essentialDetails?.avgPrice && (
                              <div className="flex gap-3">
                                <DollarSign size={14} className="text-[var(--gold)] flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className={`text-[10px] font-semibold tracking-[0.08em] uppercase ${darkMode ? 'text-white/40' : 'text-[var(--slate-muted)]'}`}>Average Price</p>
                                  <p className={`text-[12px] mt-0.5 ${darkMode ? 'text-white/80' : 'text-[var(--foreground)]'}`}>{item.essentialDetails.avgPrice}</p>
                                </div>
                              </div>
                            )}
                            {item?.essentialDetails?.idealFor && (
                              <div className="flex gap-3">
                                <Users size={14} className="text-[var(--gold)] flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className={`text-[10px] font-semibold tracking-[0.08em] uppercase ${darkMode ? 'text-white/40' : 'text-[var(--slate-muted)]'}`}>Ideal For</p>
                                  <p className={`text-[12px] mt-0.5 ${darkMode ? 'text-white/80' : 'text-[var(--foreground)]'}`}>{item.essentialDetails.idealFor}</p>
                                </div>
                              </div>
                            )}
                            {item?.essentialDetails?.founded && (
                              <div className="flex gap-3">
                                <Clock size={14} className="text-[var(--gold)] flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className={`text-[10px] font-semibold tracking-[0.08em] uppercase ${darkMode ? 'text-white/40' : 'text-[var(--slate-muted)]'}`}>Founded / Created</p>
                                  <p className={`text-[12px] mt-0.5 ${darkMode ? 'text-white/80' : 'text-[var(--foreground)]'}`}>{item.essentialDetails.founded}</p>
                                </div>
                              </div>
                            )}
                            {item?.essentialDetails?.value && (
                              <div className="flex gap-3">
                                <Tag size={14} className="text-[var(--gold)] flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className={`text-[10px] font-semibold tracking-[0.08em] uppercase ${darkMode ? 'text-white/40' : 'text-[var(--slate-muted)]'}`}>Estimated Value</p>
                                  <p className={`text-[12px] mt-0.5 ${darkMode ? 'text-white/80' : 'text-[var(--foreground)]'}`}>{item.essentialDetails.value}</p>
                                </div>
                              </div>
                            )}
                            {item?.essentialDetails?.membership && (
                              <div className="flex gap-3">
                                <Users size={14} className="text-[var(--gold)] flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className={`text-[10px] font-semibold tracking-[0.08em] uppercase ${darkMode ? 'text-white/40' : 'text-[var(--slate-muted)]'}`}>Membership</p>
                                  <p className={`text-[12px] mt-0.5 ${darkMode ? 'text-white/80' : 'text-[var(--foreground)]'}`}>{item.essentialDetails.membership}</p>
                                </div>
                              </div>
                            )}
                            {item?.essentialDetails?.category && (
                              <div className="flex gap-3">
                                <Globe size={14} className="text-[var(--gold)] flex-shrink-0 mt-0.5" />
                                <div>
                                  <p className={`text-[10px] font-semibold tracking-[0.08em] uppercase ${darkMode ? 'text-white/40' : 'text-[var(--slate-muted)]'}`}>Category</p>
                                  <p className={`text-[12px] mt-0.5 ${darkMode ? 'text-white/80' : 'text-[var(--foreground)]'}`}>{item.essentialDetails.category}</p>
                                </div>
                              </div>
                            )}
                          </div>
                          <button className="w-full mt-4 flex items-center justify-between px-4 py-2.5 border border-[var(--gold)] rounded text-[11px] font-bold tracking-[0.1em] uppercase text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white transition-all">
                            <span>ENQUIRE ABOUT THIS</span>
                            <Globe size={13} />
                          </button>
                        </div>
                        <div className={`p-4 flex items-center justify-between ${darkMode ? '' : ''}`}>
                          <div>
                            <p className={`text-[12px] font-semibold ${darkMode ? 'text-white' : 'text-[var(--foreground)]'}`}>SAVE THIS</p>
                            <p className={`text-[11px] ${darkMode ? 'text-white/50' : 'text-[var(--slate-muted)]'}`}>Add to your library</p>
                          </div>
                          <button onClick={handleSaveArticle} disabled={savingArticle} className="p-2">
                            {saved
                              ? <BookMarked size={18} className="text-[var(--gold)] fill-[var(--gold)]" />
                              : <Bookmark size={18} className={darkMode ? 'text-white/60' : 'text-[var(--slate-muted)]'} />
                            }
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* You Might Also Love */}
            <div className="px-4 lg:px-6 mb-6">
              <h2 className={`section-title mb-3 ${darkMode ? 'text-white/60' : ''}`}>YOU MIGHT ALSO LOVE</h2>
              <div className="flex flex-col gap-3">
                {article?.relatedArticles?.map((rel) => {
                  const relSlug = Object.entries(allArticles).find(([, a]) =>
                    a.title === rel.title
                  )?.[0];
                  return (
                    <Link
                      key={rel.id}
                      href={relSlug ? `/article?slug=${relSlug}` : '/article'}
                      className="flex gap-3 cursor-pointer group"
                    >
                      <div className="relative w-[80px] h-[56px] flex-shrink-0 rounded overflow-hidden">
                        <AppImage src={rel?.image} alt={rel?.alt} fill className="object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <div className="flex-1">
                        <p className={`text-[12px] font-medium leading-tight line-clamp-2 group-hover:text-[var(--gold)] transition-colors ${darkMode ? 'text-white/80' : 'text-[var(--foreground)]'}`}>{rel?.title}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>

              <div className={`mt-5 p-4 border rounded-lg ${darkMode ? 'border-white/10 bg-white/5' : 'border-[var(--border)] bg-[var(--secondary)]'}`}>
                <p className={`text-[12px] font-bold mb-1 ${darkMode ? 'text-white' : 'text-[var(--foreground)]'}`}>ASK THE PRIVATE DESK</p>
                <p className={`text-[11px] mb-3 ${darkMode ? 'text-white/50' : 'text-[var(--slate-muted)]'}`}>Can't find what you're looking for? Ask. We discover for you.</p>
                <button className="w-full py-2.5 bg-[var(--gold)] text-white text-[11px] font-bold tracking-[0.12em] uppercase rounded hover:bg-[var(--gold-dark)] transition-colors">
                  ASK A QUESTION
                </button>
              </div>
            </div>
          </div>

          {/* Paywall gradient fade + unlock CTA (shown when not subscribed) */}
          {paywallActive && (
            <div className="absolute bottom-0 left-0 right-0 h-64 bg-gradient-to-t from-white via-white/95 to-transparent flex flex-col items-center justify-end pb-8 px-6">
              <div className="flex items-center gap-2 mb-3">
                <Crown size={18} className="text-[#c9a84c]" fill="#c9a84c" fillOpacity={0.5} />
                <span className="text-[13px] font-bold text-[var(--foreground)] tracking-wide">Members-Only Content</span>
              </div>
              <p className="text-[12px] text-[var(--slate-muted)] text-center mb-4 max-w-xs">
                Subscribe to unlock this article and all 220+ exclusive discoveries.
              </p>
              <button
                onClick={() => setShowPaywall(true)}
                className="px-8 py-3 bg-gradient-to-r from-[#c9a84c] to-[#e8c96a] text-black font-black text-[12px] tracking-[0.12em] uppercase rounded-md shadow-lg hover:from-[#b8973b] hover:to-[#d4b555] transition-all flex items-center gap-2"
              >
                <Crown size={13} fill="black" fillOpacity={0.4} />
                Unlock — From ₹299/month
              </button>
            </div>
          )}
        </div>

        {/* Mobile Bottom Nav */}
        <div className={`lg:hidden fixed bottom-0 left-0 right-0 border-t flex items-center justify-around py-2 z-40 ${darkMode ? 'bg-[#0f0f0f] border-white/10' : 'bg-white border-[var(--border)]'}`}>
          {[
            { label: 'Home', icon: '⌂', href: '/editorial-discovery-feed' },
            { label: 'Discover', icon: '◎', href: '/editorial-discovery-feed' },
            { label: 'Worlds', icon: '🌐', href: '/editorial-discovery-feed' },
            { label: 'Library', icon: '📚', href: '/my-library' },
            { label: 'Profile', icon: '👤', href: '/profile' },
          ]?.map((item) => (
            <Link key={item?.label} href={item?.href} className="flex flex-col items-center gap-0.5 px-3 py-1">
              <span className="text-base">{item?.icon}</span>
              <span className={`text-[9px] ${darkMode ? 'text-white/40' : 'text-[var(--slate-muted)]'}`}>{item?.label}</span>
            </Link>
          ))}
        </div>
        <div className="lg:hidden h-16" />
      </div>

      {/* Subscription Modal */}
      {showPaywall && paywallActive && (
        <SubscriptionModal
          articleTitle={article?.title}
          onClose={() => setShowPaywall(false)}
        />
      )}
    </AppLayout>
  );
}

export default function ArticlePage() {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center min-h-screen bg-white">
        <div className="text-[var(--slate-muted)] text-sm">Loading article...</div>
      </div>
    }>
      <ArticlePageContent />
    </Suspense>
  );
}